import { PotentialIssueSeverityIcon } from '../pages/get-started/user-homepage/utils';
import { SortOrder } from '../common/types';
import { ReactNode, useMemo, useCallback, useState } from 'react';
import { Issue } from './issuesTypes';
import { DataTableVariant, DataTableSortableColumn } from '../common/table/TableTypes';
import { ACTION_BUTTONS_KEY, DataTableId } from '../common/table/utils';
import { colors, Typography } from '@sweep-io/sweep-design';
import { Box, Stack } from '@mui/material';
import capitalize from 'lodash/capitalize';
import { humanizeDate, humanizeDateVariants } from '../helpers/humanizeDate';
import IssueActions from './IssueActions';
import { resourceTypeToLabel } from './issuesHelper';
import { TruncatedTextTooltip } from '../common/TruncatedTextTooltip';
import { OnUpdateIssueStatus } from '../aiAgentCenter/aiAgentsConsts';
import { DataTableExpandableRows } from '../common/table/DataTableExpandableRows';
import ExpandableIssue from './ExpandableIssue';
import { addIssueIdToText } from '../../utils/textWithIssueId';

const NewLabel = () => (
  <Box
    sx={{
      fontSize: '12px',
      lineHeight: '16px',
      color: colors.lilac[500],
      border: `1px solid ${colors.lilac[500]}`,
      borderRadius: '2px',
      display: 'flex',
      alignItems: 'center',
      height: 'fit-content',
      paddingInline: '4px',
    }}
  >
    New
  </Box>
);

const PRIORITY_MAP = { High: 3, Medium: 2, Low: 1 };

const severityCompareFunc = (itemA: IssuesTableRow, itemB: IssuesTableRow, order: SortOrder) => {
  const value1 = PRIORITY_MAP[itemA.issue.severity] || 0;
  const value2 = PRIORITY_MAP[itemB.issue.severity] || 0;

  if (order === SortOrder.ASC) {
    return value1 - value2;
  } else {
    return value2 - value1;
  }
};

const createStringCompareFunc = (fieldAccessor: (item: IssuesTableRow) => string) => {
  return (itemA: IssuesTableRow, itemB: IssuesTableRow, order: SortOrder) => {
    const value1 = fieldAccessor(itemA);
    const value2 = fieldAccessor(itemB);
    const multiplyBy = order === SortOrder.ASC ? 1 : -1;
    return value1.toLowerCase().localeCompare(value2.toLowerCase()) * multiplyBy;
  };
};

const NameCompareFunc = createStringCompareFunc((item) => item.issue.resourceName);
const resourceTypeCompareFunc = createStringCompareFunc((item) => item.issue.resourceType);
const bestPracticeNameCompareFunc = createStringCompareFunc((item) => item.issue.bestPracticeName);
const statusCompareFunc = createStringCompareFunc((item) => item.issue.status);

const lastDetectedCompareFunc = (
  itemA: IssuesTableRow,
  itemB: IssuesTableRow,
  order: SortOrder,
) => {
  const value1 = new Date(itemA.issue.createdAt).getTime();
  const value2 = new Date(itemB.issue.createdAt).getTime();

  if (order === SortOrder.ASC) {
    return value1 - value2;
  } else {
    return value2 - value1;
  }
};

const getSortedRows = (rows: IssuesTableRow[], sortBy: string, sortOrder: SortOrder) => {
  if (!sortBy) return rows;

  const sortedRows = [...rows];

  switch (sortBy) {
    case 'resourceName':
      return sortedRows.sort((a, b) => NameCompareFunc(a, b, sortOrder));
    case 'severity':
      return sortedRows.sort((a, b) => severityCompareFunc(a, b, sortOrder));
    case 'resourceType':
      return sortedRows.sort((a, b) => resourceTypeCompareFunc(a, b, sortOrder));
    case 'bestPracticeName':
      return sortedRows.sort((a, b) => bestPracticeNameCompareFunc(a, b, sortOrder));
    case 'status':
      return sortedRows.sort((a, b) => statusCompareFunc(a, b, sortOrder));
    case 'lastDetected':
      return sortedRows.sort((a, b) => lastDetectedCompareFunc(a, b, sortOrder));
    default:
      return rows;
  }
};

type IssuesTableRow = {
  issue: Issue;
  id: string;
  resourceName: ReactNode;
  resourceType: ReactNode;
  bestPracticeName: ReactNode;
  severity: ReactNode;
  status: string;
  lastDetected: ReactNode;
  [ACTION_BUTTONS_KEY]: ReactNode;
  expandableContent: ReactNode;
};

const columns: DataTableSortableColumn<IssuesTableRow>[] = [
  {
    field: 'resourceName',
    headerName: 'Name',
    showSortIcon: true,
    width: '280px',
  },
  {
    field: 'resourceType',
    headerName: 'Type',
    showSortIcon: true,
    width: '120px',
    minWidth: '120px',
  },
  {
    field: 'bestPracticeName',
    headerName: 'Issue',
    showSortIcon: true,
    width: '240px',
  },
  {
    field: 'severity',
    headerName: 'Impact',
    showSortIcon: true,
    width: '117px',
    minWidth: '117px',
  },
  {
    field: 'status',
    headerName: 'Status',
    showSortIcon: true,
    width: '50px',
    minWidth: '50px',
  },
  {
    field: 'lastDetected',
    headerName: 'Last Detected',
    showSortIcon: true,
    width: '200px',
    minWidth: '200px',
  },
];

const IssuesTable = ({
  issues,
  onUpdateIssueStatus,
  onTextSelection,
}: {
  issues: Issue[];
  onUpdateIssueStatus: OnUpdateIssueStatus;
  onTextSelection?: (text: string | null) => void;
}) => {
  const [expandedRowId, setExpandedRowId] = useState<string | undefined>();
  const [sortProps, setSortProps] = useState<{ sortBy: string; sortOrder: SortOrder }>({
    sortBy: 'resourceName',
    sortOrder: SortOrder.ASC,
  });

  // Create a wrapper function that adds issueId to selected text
  const createTextSelectionHandler = useCallback(
    (issueId: string) => {
      return (selectedText: string | null) => {
        if (selectedText && onTextSelection) {
          const textWithIssueId = addIssueIdToText(selectedText, issueId);
          onTextSelection(textWithIssueId);
        } else if (onTextSelection) {
          onTextSelection(selectedText);
        }
      };
    },
    [onTextSelection],
  );

  const _rows: IssuesTableRow[] = useMemo(() => {
    const rows =
      issues?.map((issue) => ({
        id: issue.id,
        resourceName: (
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            sx={{
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <TruncatedTextTooltip
              color={
                (expandedRowId === issue.id ? colors.blue[500] : colors.grey[900]) + '!important'
              }
            >
              {issue.resourceName}
            </TruncatedTextTooltip>
            {issue.isNew && (
              <Box marginLeft="auto">
                <NewLabel />
              </Box>
            )}
          </Stack>
        ),
        resourceType: (
          <TruncatedTextTooltip>{resourceTypeToLabel[issue.resourceType]}</TruncatedTextTooltip>
        ),
        bestPracticeName: <TruncatedTextTooltip>{issue.bestPracticeName}</TruncatedTextTooltip>,
        severity: (
          <Stack direction="row" width="100px" alignItems="center" gap={0.5}>
            <Typography variant="body" color={colors.grey[700]}>
              {issue.severity}
            </Typography>
            {PotentialIssueSeverityIcon[issue.severity]}
          </Stack>
        ),
        status: capitalize(issue.status),
        lastDetected: (
          <Typography variant="body">
            {humanizeDate({
              dateOrTimestamp: issue.createdAt,
              variant: humanizeDateVariants.TWO_DIGITS_WITH_TIME,
            })}
          </Typography>
        ),
        [ACTION_BUTTONS_KEY]: <IssueActions issue={issue} onUpdateStatus={onUpdateIssueStatus} />,
        issue,
        expandableContent: (
          <ExpandableIssue
            content={issue.description}
            onTextSelection={createTextSelectionHandler(issue.id)}
          />
        ),
      })) ?? [];

    return getSortedRows(rows, sortProps.sortBy, sortProps.sortOrder);
  }, [
    createTextSelectionHandler,
    expandedRowId,
    issues,
    onUpdateIssueStatus,
    sortProps.sortBy,
    sortProps.sortOrder,
  ]);

  const tableEmptyStateJsx = (
    <Stack sx={{ marginBlock: 3, justifyContent: 'center', alignItems: 'center' }}>
      <Typography variant="h5" color={colors.grey[700]}>
        No issues found
      </Typography>
    </Stack>
  );

  const commonTableProps = {
    tableId: DataTableId.IssuesTable,
    expandedRowId,
    setExpandedRowId,
    columns,
    rows: _rows,
    variant: DataTableVariant.medium,
    hideChevronButton: true,
    stickyHeader: true,
    tableEmptyStateJsx,
    isVirtualScroll: true,
    onSortChange: setSortProps,
    defaultSortState: sortProps,
  };

  return (
    <Stack
      sx={{
        overflow: 'hidden', //to fix visibility of the corner radius
        flex: 1,
        '& td:first-of-type:after, & th:first-of-type:after': {
          //remove the divider
          display: 'none',
        },
        '& *::-webkit-scrollbar': {
          width: '4px',
          height: '4px',
        },
        '& *::-webkit-scrollbar-thumb': {
          background: colors.grey[120] + '!important',
        },
      }}
    >
      <DataTableExpandableRows {...commonTableProps} tableMaxHeight="100%" />
    </Stack>
  );
};

export default IssuesTable;
